import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Divider, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import moment from 'moment';
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from "uuid";
import { ReactComponent as BuildingIcon } from "../../../assets/icons/gray-building.svg";
import { AuthContext } from '../../../contexts/AuthContext';
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import { UnassignedContentData } from '../../../contexts/InboxContext';
import { getUserEmail } from '../../../helpers/authUser';
import { AttachmentType, EmailDeal } from '../../../hooks/useEmailDealsExtraction';
import useStaticDashboard from '../../../hooks/useStaticDashboard';
import { createDashboardFunc, getAllUsersFunc } from '../../../lib/helper';
import theme from '../../../theme';
import { Dashboard, DealOwner, UnassignedContent } from '../../../types/files';
import configuredAsyncComponentLoader from '../../templates/loader';
import WebsiteUrlInput from '../crunchbase-modal/WebsiteUrlInput';
import DashboardsSearchOrCreate from '../dashboards-search-create/DashboardsSearchOrCreate';
import AddIcon from '@mui/icons-material/Add';
import { ReactComponent as LinkIcon } from "../../../assets/icons/link.svg";
import CloseIcon from "@mui/icons-material/Close";

const UnassignedEmailBody = configuredAsyncComponentLoader(() => import("./UnassignedEmailBody"));
const UnassignedEmailDeals = configuredAsyncComponentLoader(() => import("./UnassignedEmailDeals"));

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        height: 'fit-content',
        background: '#fefefe',
        "&:hover": {
            background: theme.colors.primary['50'],
        }
    },
    expanded: {
        background: theme.colors.primary['50'],
        borderLeft: `4px solid #0d9486`,
    },
    title: {
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#000',
    },
    addTo: {
        fontSize: '0.95rem',
        fontFamily: 'Lato',
        fontWeight: 'bold',
        color: theme.colors.neutral['800'],
    },
    chip: {
        height: 30,
        width: 120,
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: 500,
        backgroundColor: theme.colors.orange['50'],
        color: theme.colors.orange['500'],
    },
    lastUpdatedDate: {
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: 500,
        color: theme.colors.neutral['600'],
        whiteSpace: 'nowrap',
    },
    inputField: {
        minWidth: 'fit-content',
        "& .MuiOutlinedInput-root": {
            minWidth: 250,
            height: 40,
            borderRadius: 8,
            padding: '0 8px !important',
            background: '#fff',
            "& > input": {
                padding: 'unset !important',
                fontSize: '1rem',
            },
            "& fieldset": {
              	borderColor: 'darkgray !important',
            },
            "&:hover fieldset": {
              	borderColor: 'black !important',
            },
            "&.Mui-focused fieldset": {
              	borderColor: `${theme.palette.primary.main} !important`,
            },
        },
    },
    addAttachButton: {
      width: 'auto',
      minWidth: 100,
      height: 45,
      borderRadius: 8,
      color: theme.palette.primary.main,
      fontFamily: 'Inter',
      fontSize: '0.9rem',
      fontWeight: 500,
      textTransform: 'none',
      transition: 'ease-in-out 300ms',
    },
    discardButton: {
      width: 'auto',
      minWidth: 100,
      height: 45,
      borderRadius: 8,
      borderColor: theme.colors.neutral['400'],
      color: theme.colors.neutral['700'],
      fontFamily: 'Inter',
      fontSize: '0.9rem',
      fontWeight: 500,
      textTransform: 'none',
      transition: 'ease-in-out 300ms',
      '&:hover': {
        backgroundColor: theme.colors.neutral['100'],
        borderColor: theme.colors.neutral['600'],
      }
    },
    addButton: {
      width: 'auto',
      minWidth: 120,
      height: 50,
      borderRadius: 8,
      backgroundColor: theme.colors.primary['600'],
      color: 'white',
      fontFamily: 'Lato',
      fontSize: '0.9rem',
      fontWeight: 600,
      textTransform: 'none',
      transition: 'ease-in-out 300ms',
    },
    attachButton: {
      width: 'auto',
      minWidth: 120,
      height: 50,
      borderRadius: 8,
      color: theme.palette.primary.main,
      fontFamily: 'Lato',
      fontSize: '0.9rem',
      fontWeight: 600,
      textTransform: 'none',
      transition: 'ease-in-out 300ms',
    },
    dismissButton: {
      width: 'auto',
      minWidth: 120,
      height: 50,
      borderRadius: 8,
      borderColor: theme.colors.neutral['400'],
      color: theme.colors.neutral['700'],
      fontFamily: 'Lato',
      fontSize: '0.9rem',
      fontWeight: 600,
      textTransform: 'none',
      transition: 'ease-in-out 300ms',
      '&:hover': {
        backgroundColor: theme.colors.neutral['100'],
        borderColor: theme.colors.neutral['600'],
      }
    },
    notificationBlock: {
        width: '100%',
        height: 150,
        transition: 'height .3s ease',
    },
    notificationIcon: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        fill: theme.palette.primary.main,
    },
    notificationText: {
        fontFamily: 'Inter',
        fontWeight: 'bold',
        color: theme.colors.neutral['700'],
    },
    ownership: {
        display: '-webkit-box',
        width: 'fit-content',
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: '#666666',
        overflow: 'hidden',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        wordBreak: 'break-all',
    },
    owner: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 500,
        color: '#7e7e7e',
    },
    preview :{
        display: '-webkit-box',
        width: 'fit-content',
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        color: '#aeaeae',
        overflow: 'hidden',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        wordBreak: 'break-all',
    },
    noDeals: {
        width: 'fit-content',
        height: 'fit-content',
        padding: '4px 12px',
        borderRadius: 16,
        background: theme.colors.error['50'],
    },
    hasDeals: {
        width: 'fit-content',
        height: 'fit-content',
        padding: '4px 12px',
        borderRadius: 16,
        background: theme.colors.yellow['50'],
    },
    icon: {
        width: 24,
        height: 24,
        padding: 'unset',
    },
    iconNoCompanies: {
        marginTop: 4,
        padding: 8,
        borderRadius: 12,
        background: theme.colors.error['100'],
    },
    iconHasCompanies: {
        marginTop: 4,
        padding: 8,
        borderRadius: 12,
        background: theme.colors.warning['100'],
    },
}));

const UnassignedEmailContent: React.FC<{
    contentData: UnassignedContentData,
    createdAt?: string,
    onSave?: (content: UnassignedContent, dashboard?: Dashboard, deals?: EmailDeal[]) => Promise<any>,
    onDelete?: (content: UnassignedContent) => void,
}> = ({ contentData, createdAt, onSave, onDelete }) => {
    const classes = useStyles();
    const {user, userGroup} = useContext(AuthContext);
    const { defaultStatus, externalSync } = useContext(GroupSettingsContext);
    const { dashboards } = useContext(DashboardsContext);
    const { saveWebQueryAnswer } = useStaticDashboard();
    const [loading, setLoading] = useState<boolean>(false);
    const [newCompany, setNewCompany] = useState<boolean>(false);
    const [attachCompany, setAttachCompany] = useState<boolean>(false);
    const [dashboard, setDashboard] = useState<Dashboard|null>(null);
    const [dashboardTitle, setDashboardTitle] = useState<string>('');
    const [customUrl, setCustomUrl] = useState<string>('');
    const [isNotificationVisible, setIsNotificationVisible] = useState<boolean>(false);
    const [notification, setNotification] = useState<string|null>(null);
    const [isExpanded, setExpanded] = useState<boolean>(false);
    const [owners, setOwners] = useState<DealOwner[]>([]);

    const { content, deals, exists, excluded } = contentData;

    const attachments = useMemo(() =>
        content.attachments?.map(attachment => ({url: '', name: attachment, contentType: 'application/pdf' })) as AttachmentType[]
    , [content.attachments]);

    const excludedDeals = useMemo(() => deals.filter((_, i) => excluded[i]), [deals, excluded]);

    const existingDeals = useMemo(() => deals.filter((_, i) => exists[i]), [deals, exists]);

    const contentDeals = useMemo(() => deals.filter((_, i) => !excluded[i] && !exists[i]), [deals, excluded, exists]);

    const debounced = useDebouncedCallback((value) => {
        setDashboardTitle(value);
        handleSearch(value);
    }, 500, { maxWait: 2000 });

    const handleSearch = useCallback((search: string) =>
        setDashboard(dashboards.find(d => d.title.toLowerCase() === search.toLowerCase()) ?? null)
    , [dashboards]);

    const handleClear = useCallback(() => {
        setNewCompany(false);
        setAttachCompany(false);
        setDashboard(null);
        setDashboardTitle('');
        setCustomUrl('');
        setIsNotificationVisible(false);
        setNotification(null);
        setLoading(false);
    }, []);

    const handleSaveDeals = useCallback(async (deals: EmailDeal[]) => {
        setLoading(true);
        setNotification(`New deals successfully added`);
        setIsNotificationVisible(true);

        await new Promise<void>((resolve) => {
            const timeout = setTimeout(() => {
                clearTimeout(timeout);
                onSave?.(content, undefined, deals).then(() => {
                    handleClear();
                    resolve();
                });
            }, 1000);
        });
    }, [content, handleClear, onSave]);

    const handleAttach = useCallback(() => {
        setLoading(true);
        setNotification(`${content.type === 'CALL' ? 'Call' : 'Email'} successfully attached to ${dashboard?.title || 'dashboard'}`);
        setIsNotificationVisible(true);

        const timeout = setTimeout(async () => {
            clearTimeout(timeout);
            await onSave?.(content, dashboard!);
            handleClear();
        }, 3000);
    }, [content, dashboard, handleClear, onSave]);

    const handleCreate = useCallback(() => {
        const lookUpOwner = owners.find(own =>
            own.email?.toLowerCase() === user.attributes.email?.trim()?.toLowerCase());
        const newDashboard = {
            createdAt: (new Date()).toISOString(),
            updatedAt: (new Date()).toISOString(),
            userId: getUserEmail(user!),
            email: getUserEmail(user!),
            group: userGroup!,
            project: userGroup!,
            id: uuidv4(),
            title: dashboardTitle,
            documents: [],
            selections: [],
            screenshots: [],
            notes: [],
            lastUpdatedBy: '',
            shouldRefresh: false,
            externalLinks: null,
            shouldSyncExternally: externalSync,
            refreshData: null,
            summary: '',
            status: defaultStatus  || 'new',
            investmentStage: null,
            dealOwner: {
                email: lookUpOwner?.email,
                name: lookUpOwner?.name,
            },
        } as Dashboard;

        setLoading(true);
        createDashboardFunc(newDashboard).then(async () => {
            await saveWebQueryAnswer(newDashboard, customUrl);
            setNotification(`${newDashboard?.title || 'dashboard'} successfully added`);
            setIsNotificationVisible(true);

            const timeout = setTimeout(async () => {
                clearTimeout(timeout);
                await onSave?.(content, newDashboard);
                handleClear();
            }, 3000);
        });
    // eslint-disable-next-line
    }, [content, customUrl, dashboard, dashboardTitle, defaultStatus, externalSync, user, userGroup]);

    useEffect(()=>{
        if (userGroup) {
            getAllUsersFunc(userGroup).then(users => setOwners(users.map((user: any) => ({
                name: `${user.firstName} ${user.lastName}`,
                email: user.email,
            }) as DealOwner)));
        }
    }, [userGroup]);

    if (isNotificationVisible) {
        return (<>
            <Stack className={classes.container} spacing={1} width="100%" pb={1}>
                <Divider sx={{ width: '100%' }}/>
                <Stack spacing={2} className={classes.notificationBlock} alignItems="center" justifyContent="center">
                    <CheckCircleRoundedIcon className={classes.notificationIcon}/>
                    <Typography className={classes.notificationText}> {notification} </Typography>
                </Stack>
            </Stack>
        </>);
    }

    if (!contentData.content)
        return (<></>);

    return (<>
        <Stack className={classNames(classes.container, isExpanded && classes.expanded)}>
            <Divider sx={{ width: '100%' }}/>
            <Box height={8} />
            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start" p={2} width="100%"
                onClick={(e) => { e.stopPropagation(); setExpanded(prev => !prev); }}>
                {!contentDeals.length ? (
                    <Stack className={classes.iconNoCompanies}>
                        <ErrorOutlineIcon sx={{ width: 24, height: 24, color: theme.colors.error['500'] }} />
                    </Stack>
                ) : (
                    <Stack className={classes.iconHasCompanies} sx={{ '& svg path': { stroke: theme.colors.warning['600'] } }}>
                        <BuildingIcon style={{ width: 24, height: 24 }} />
                    </Stack>
                )}
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" pl="4px" width="100%">
                    <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="flex-start">
                        <Typography component="span" className={classes.title} display="inline">
                            {content?.subject?.replace('Fwd:', '')?.replace('Fw:', '') || '<Untitled>'}
                        </Typography>
                        <Typography className={classes.ownership}>
                            {'From: '}
                            <Typography component="span" className={classes.owner} display="inline" pl="4px">
                                {!!content.from ? content.from.replace(/<.*?>/g, '').trim() : 'N/A'}
                            </Typography>
                        </Typography>
                        <Typography className={classes.ownership}>
                            {'Source: '}
                            <Typography component="span" className={classes.owner} display="inline" pl="4px"
                                sx={{ textTransform: `${!!content.dealSource ? 'capitalize' : 'lowercase'} !important`}}>
                                {content.dealSource?.name || content.source || 'N/A'}
                            </Typography>
                        </Typography>
                        <Box height={8} />
                        {!contentDeals.length ? (
                            <Stack direction="row" className={classes.noDeals} spacing={1} alignItems="center" justifyContent="flex-start">
                                <ErrorOutlineIcon sx={{ width: 20, height: 20, color: theme.colors.error['500'] }} />
                                <Typography fontFamily="Inter" fontSize="0.9rem" fontWeight={500} color={theme.colors.error['500']}>
                                    {`No company found`}
                                </Typography>
                            </Stack>
                        ) : (
                            <Stack direction="row" className={classes.hasDeals} spacing={1} alignItems="center" justifyContent="flex-start">
                                <Box sx={{ '& svg path': { stroke: theme.colors.warning['600'] } }}>
                                    <BuildingIcon style={{ width: 20, height: 20 }} />
                                </Box>
                                <Typography fontFamily="Inter" fontSize="0.9rem" fontWeight={500} color={theme.colors.warning['600']}>
                                    {`${contentDeals.length} compan${contentDeals.length > 1 ? 'ies' : 'y'} found`}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                    <Stack direction="row" spacing="4px" alignItems="center" justifyContent="flex-end">
                        {!!createdAt && (
                            <Typography className={classes.lastUpdatedDate}>{`${moment(createdAt).fromNow()}`}</Typography>
                        )}
                        <IconButton className={classes.icon}
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpanded(prev => !prev);
                            }} disableRipple>
                          <ExpandMoreIcon style={{ transform: !isExpanded ? 'none' : 'rotate(180deg)' }} />
                        </IconButton>
                    </Stack>
                </Stack>
            </Stack>
            {isExpanded && (
                <Box pl={8} pr={1} my={2}>
                    <UnassignedEmailBody emailBody={content?.body} isExpanded={isExpanded} />
                </Box>
            )}
            {!!excludedDeals.length && (<>
                <UnassignedEmailDeals
                    emailDeals={excludedDeals}
                    attachments={attachments}
                    onSave={handleSaveDeals}
                    onDiscard={() => onDelete?.(content)}
                    excluded />
            </>)}
            {!!existingDeals.length && (<>
                <UnassignedEmailDeals
                    emailDeals={existingDeals}
                    attachments={attachments}
                    onSave={handleSaveDeals}
                    onDiscard={() => onDelete?.(content)}
                    existing />
            </>)}
            {!!contentDeals.length && (<>
                <UnassignedEmailDeals
                    emailDeals={contentDeals}
                    attachments={attachments}
                    onSave={handleSaveDeals}
                    onDiscard={() => onDelete?.(content)} />
            </>)}
            {!contentDeals.length && !existingDeals.length && !excludedDeals.length && (<>
                {(newCompany && !attachCompany) && (<>
                    <Stack direction="column" spacing={2} alignItems="flex-start" width="100%" pl={8} pb={2}>
                        <Typography className={classes.addTo}> {'Company Name*'} </Typography>
                        <Box width="100%" pr={3}>
                            <TextField
                                variant="outlined"
                                className={classes.inputField}
                                placeholder='Enter company name'
                                onChange={(e) => debounced(e.target.value)}
                                sx={{ width: '100%' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ "svg": {"path": { stroke: '#aeaeae' }} }}>
                                            <BuildingIcon style={{ width: 24, height: 24 }} />
                                        </InputAdornment>
                                    )}}/>
                        </Box>
                        <Typography className={classes.addTo}>
                            {'Company Website '}
                            <Typography color={theme.colors.neutral['500']} display="inline">{'(optional)'}</Typography>
                        </Typography>
                        <Box width="100%" pr={3} sx={{ "& > div > .MuiInputBase-root" : { borderRadius: '8px !important'}, "svg": { fill: '#aeaeae' }}}>
                            <WebsiteUrlInput placeholder={'https://example.com'} customUrl={customUrl} onCustomUrl={setCustomUrl} inlineCaption />
                        </Box>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start" width="100%">
                            <LoadingButton variant="outlined"
                                className={classes.addAttachButton}
                                onClick={handleCreate}
                                disabled={!dashboardTitle}
                                loading={loading}>
                                {'Add'}
                            </LoadingButton>
                            <Button variant="outlined"
                                className={classes.discardButton}
                                onClick={handleClear}
                                disabled={loading}>
                                {'Discard'}
                            </Button>
                        </Stack>
                        <Divider sx={{ width: '100%' }}/>
                        <Typography>{'Or choose another action:'}</Typography>
                        <Stack direction="row" spacing={5} alignItems="center" justifyContent="center" width="100%">
                            <Button variant="outlined"
                                className={classes.attachButton}
                                startIcon={
                                    <InputAdornment position="start" sx={{ "svg": {"path": { stroke: theme.palette.primary.main }} }}>
                                        <LinkIcon style={{ width: 24, height: 24 }} />
                                    </InputAdornment>
                                } onClick={() => {setNewCompany(false); setAttachCompany(true);}}>
                                {'Attach to Existing'}
                            </Button>
                            <Button variant="outlined"
                                className={classes.dismissButton}
                                startIcon={<CloseIcon />}
                                onClick={() => onDelete?.(content)}>
                                {'Dismiss Email'}
                            </Button>
                        </Stack>
                    </Stack>
                </>)}
                {(!newCompany && attachCompany) && (<>
                    <Stack direction="column" spacing={2} alignItems="flex-start" width="100%" pl={8} pb={2}>
                        <Typography className={classes.addTo}> {'Attach to:'} </Typography>
                        <Box width="100%" pr={3}>
                            <DashboardsSearchOrCreate search autoComplete squareFit onSearch={handleSearch} />
                        </Box>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start" width="100%">
                            <LoadingButton variant="outlined"
                                className={classes.addAttachButton}
                                disabled={!dashboard}
                                loading={loading}
                                onClick={handleAttach}>
                                {'Attach'}
                            </LoadingButton>
                            <Button variant="outlined"
                                className={classes.discardButton}
                                onClick={handleClear}
                                disabled={loading}>
                                {'Discard'}
                            </Button>
                        </Stack>
                        <Divider sx={{ width: '100%' }}/>
                        <Typography>{'Or choose another action:'}</Typography>
                        <Stack direction="row" spacing={5} alignItems="center" justifyContent="center" width="100%">
                            <Button variant="contained"
                                className={classes.addButton}
                                startIcon={<AddIcon />}
                                onClick={() => {setNewCompany(true); setAttachCompany(false); }}>
                                {'Add New Company'}
                            </Button>
                            <Button variant="outlined"
                                className={classes.dismissButton}
                                startIcon={<CloseIcon />}
                                onClick={() => onDelete?.(content)}>
                                {'Dismiss Email'}
                            </Button>
                        </Stack>
                    </Stack>
                </>)}
                {(!newCompany && !attachCompany) && (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" px={2} pb={2} pt={2}>
                        <Button variant="contained"
                            className={classes.addButton}
                            startIcon={<AddIcon />}
                            onClick={() => setNewCompany(true)}>
                            {'Add New Company'}
                        </Button>
                        <LoadingButton variant="outlined"
                            className={classes.attachButton}
                            startIcon={
                                <InputAdornment position="start" sx={{ "svg": {"path": { stroke: theme.palette.primary.main }} }}>
                                    <LinkIcon style={{ width: 28, height: 28 }} />
                                </InputAdornment>
                            } onClick={() => setAttachCompany(true)}>
                            {'Attach to Existing'}
                        </LoadingButton>
                        <Button variant="outlined"
                            className={classes.dismissButton}
                            startIcon={<CloseIcon />}
                            onClick={() => onDelete?.(content)}>
                            {'Dismiss Email'}
                        </Button>
                    </Stack>
                )}
            </>)}
        </Stack>
    </>);
}

export default memo(UnassignedEmailContent);